import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Loader2, Copy, Printer, Download, QrCode, KeyRound, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ChangePasswordCard from "@/components/admin/ChangePasswordCard";
import ChangeEmailCard from "@/components/admin/ChangeEmailCard";

// Petite confirmation inline (mot de passe + code 2FA) pour les actions sensibles.
function ConfirmAction({ testidPrefix, trigger, triggerClass, danger, onConfirm, disabled, disabledHint }) {
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!pwd || !code.trim()) return toast.error("Mot de passe et code requis");
    setBusy(true);
    try {
      await onConfirm(pwd, code.trim());
      setOpen(false);
      setPwd("");
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  if (disabled) {
    return (
      <div>
        <button data-testid={`${testidPrefix}-trigger`} disabled className="flex cursor-not-allowed items-center gap-2 rounded-full border border-amber-800/15 px-5 py-2.5 text-sm font-medium text-stone-mute opacity-70">
          {trigger}
        </button>
        {disabledHint && <p className="mt-2 text-xs text-bark-soft">{disabledHint}</p>}
      </div>
    );
  }

  if (!open) {
    return (
      <button data-testid={`${testidPrefix}-trigger`} onClick={() => setOpen(true)} className={triggerClass}>
        {trigger}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-xl bg-sand/50 p-4 sm:flex-row sm:items-end">
      <label className="flex-1 text-xs font-medium uppercase tracking-wider text-stone-mute">
        Mot de passe
        <input
          data-testid={`${testidPrefix}-password`}
          type="password"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          className="mt-1 w-full rounded-xl border border-amber-800/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-deep"
        />
      </label>
      <label className="text-xs font-medium uppercase tracking-wider text-stone-mute">
        Code 2FA
        <input
          data-testid={`${testidPrefix}-code`}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="mt-1 w-40 rounded-xl border border-amber-800/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-deep"
          placeholder="••••••"
        />
      </label>
      <div className="flex gap-2">
        <button
          data-testid={`${testidPrefix}-confirm`}
          onClick={run}
          disabled={busy}
          className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-60 ${danger ? "bg-terra hover:bg-terra/90" : "bg-amber-deep hover:bg-amber-deep/90"}`}
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : null} Confirmer
        </button>
        <button onClick={() => setOpen(false)} className="rounded-full px-4 py-2.5 text-sm font-medium text-bark-soft hover:text-bark">Annuler</button>
      </div>
    </div>
  );
}

export default function AdminSecurity() {
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState(null); // {enabled, required}
  const [step, setStep] = useState("idle"); // idle | enrolling | codes
  const [qr, setQr] = useState(null);
  const [uri, setUri] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [emailed, setEmailed] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");

  const loadStatus = () => api.get("/auth/2fa/status").then(({ data }) => setStatus(data));

  useEffect(() => {
    loadStatus();
  }, []);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enroll/start");
      setQr(data.qr_png);
      setUri(data.otpauth_uri);
      setStep("enrolling");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de démarrer l'activation");
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async () => {
    if (!code.trim()) return toast.error("Saisissez le code de votre application");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enroll/confirm", { code: code.trim() });
      showCodes(data);
      setCode("");
      toast.success("Double authentification activée");
      loadStatus();
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code incorrect");
    } finally {
      setBusy(false);
    }
  };

  const showCodes = (data) => {
    setRecoveryCodes(data.recovery_codes || []);
    setEmailed(!!data.emailed);
    setRecoveryEmail(data.recovery_email || "");
    setStep("codes");
  };

  const regenerate = async (password, code2) => {
    try {
      const { data } = await api.post("/auth/2fa/recovery/regenerate", { password, code: code2 });
      showCodes(data);
      toast.success("Nouveaux codes de secours générés");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Régénération impossible");
      throw err;
    }
  };

  const enforce = async (password, code2, required) => {
    try {
      await api.post("/auth/2fa/enforce", { password, code: code2, required });
      toast.success(required ? "2FA rendue obligatoire" : "Obligation retirée");
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action impossible");
      throw err;
    }
  };

  const disable = async (password, code2) => {
    try {
      await api.post("/auth/2fa/disable", { password, code: code2 });
      toast.success("Double authentification désactivée");
      setStep("idle");
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Désactivation impossible");
      throw err;
    }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    toast.success("Codes copiés");
  };
  const downloadCodes = () => {
    const blob = new Blob([`Codes de secours 2FA — Pétales de Miel\n\n${recoveryCodes.join("\n")}\n\nChaque code ne peut servir qu'une seule fois.`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "codes-de-secours-2fa.txt";
    a.click();
    URL.revokeObjectURL(url);
  };
  const printCodes = () => {
    const w = window.open("", "_blank");
    w.document.write(`<pre style="font:16px monospace;padding:24px">Codes de secours 2FA — Pétales de Miel\n\n${recoveryCodes.join("\n")}\n\nChaque code ne peut servir qu'une seule fois.</pre>`);
    w.document.close();
    w.print();
  };

  if (status === null) {
    return (
      <div className="flex items-center justify-center gap-3 py-32 text-bark-soft">
        <Loader2 className="animate-spin text-amber-deep" size={26} />
        <span className="font-serif text-lg">Chargement…</span>
      </div>
    );
  }

  return (
    <div data-testid="admin-security-page" className="max-w-2xl space-y-6">
      <div>
        <h1 className="font-serif text-3xl font-semibold text-bark">Sécurité — Double authentification</h1>
        <p className="mt-1 text-sm text-bark-soft">
          Protégez l'accès administrateur avec un code temporaire généré par une application (Google Authenticator, Authy…).
        </p>
      </div>
      {!status.enabled && (
        <p data-testid="twofa-mandatory-banner" className="rounded-2xl border border-terra/30 bg-terra/10 p-4 text-sm font-medium text-terra">
          La double authentification est obligatoire pour accéder à l'administration. Activez-la ci-dessous : scannez le QR code avec votre application, puis saisissez le code à 6 chiffres.
        </p>
      )}
      <ChangeEmailCard />
      <ChangePasswordCard />

      {/* Bandeau de statut */}
      <div className={`flex items-center gap-3 rounded-2xl border p-5 ${status.enabled ? "border-sage/40 bg-sage/5" : "border-amber-800/15 bg-white"}`}>
        <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${status.enabled ? "bg-sage/15 text-sage" : "bg-stone-mute/10 text-stone-mute"}`}>
          {status.enabled ? <ShieldCheck size={22} /> : <ShieldOff size={22} />}
        </span>
        <div>
          <p data-testid="twofa-status" className="flex items-center gap-2 font-serif text-lg font-semibold text-bark">
            {status.enabled ? "2FA activée" : "2FA désactivée"}
            {status.enabled && status.required && (
              <span data-testid="twofa-required-badge" className="flex items-center gap-1 rounded-full bg-amber-deep/10 px-2.5 py-0.5 text-xs font-semibold text-amber-deep"><Lock size={11} /> Obligatoire</span>
            )}
          </p>
          <p className="text-sm text-bark-soft">
            {status.enabled
              ? "Un code de votre application est demandé à chaque connexion."
              : "Votre connexion repose uniquement sur le mot de passe."}
          </p>
        </div>
      </div>

      {/* Activation */}
      {!status.enabled && step === "idle" && (
        <div className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
          <p className="text-sm text-bark-soft">
            Activez la 2FA pour ajouter une couche de sécurité. Vous aurez besoin d'une application d'authentification sur votre téléphone.
          </p>
          <button
            data-testid="twofa-enable-button"
            onClick={startEnroll}
            disabled={busy}
            className="mt-4 flex items-center gap-2 rounded-full bg-amber-deep px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-deep/90 disabled:opacity-60"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Activer la 2FA
          </button>
        </div>
      )}

      {step === "enrolling" && (
        <div data-testid="twofa-enroll-panel" className="rounded-2xl border border-amber-deep/30 bg-white p-6 shadow-sm">
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
            <div className="shrink-0 rounded-xl border border-amber-800/15 p-3">
              {qr && <img data-testid="twofa-qr" src={qr} alt="QR code 2FA" className="h-48 w-48" />}
            </div>
            <div className="flex-1 space-y-3">
              <p className="flex items-center gap-2 font-serif text-lg font-semibold text-bark"><QrCode size={18} /> Scannez ce QR code</p>
              <ol className="list-decimal space-y-1 pl-5 text-sm text-bark-soft">
                <li>Ouvrez Google Authenticator (ou Authy) sur votre téléphone.</li>
                <li>Ajoutez un compte en scannant ce QR code.</li>
                <li>Saisissez le code à 6 chiffres affiché pour confirmer.</li>
              </ol>
              <details className="text-xs text-stone-mute">
                <summary className="cursor-pointer">Impossible de scanner ? Saisie manuelle</summary>
                <code className="mt-1 block break-all rounded bg-sand/60 p-2">{uri}</code>
              </details>
              <div className="pt-1">
                <label className="text-xs font-medium uppercase tracking-wider text-stone-mute">Code à 6 chiffres</label>
                <div className="mt-1 flex gap-2">
                  <input
                    data-testid="twofa-confirm-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    inputMode="numeric"
                    placeholder="••••••"
                    className="w-40 rounded-xl border border-amber-800/20 px-3 py-2.5 text-center text-lg tracking-[0.3em] outline-none focus:border-amber-deep"
                  />
                  <button
                    data-testid="twofa-confirm-button"
                    onClick={confirmEnroll}
                    disabled={busy}
                    className="flex items-center gap-2 rounded-full bg-amber-deep px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-deep/90 disabled:opacity-60"
                  >
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Confirmer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {step === "codes" && (
        <div data-testid="twofa-recovery-panel" className="rounded-2xl border border-sage/40 bg-sage/5 p-6 shadow-sm">
          <p className="flex items-center gap-2 font-serif text-lg font-semibold text-bark"><KeyRound size={18} /> Vos 8 codes de secours</p>
          <p className="mt-1 text-sm text-bark-soft">
            Conservez-les précieusement : chacun ne fonctionne qu'une seule fois, en cas de perte de votre téléphone.
            Ils ne seront <strong>plus jamais affichés</strong>. Toute génération remplace les codes précédents.
          </p>
          {recoveryEmail && (
            <p className={`mt-2 rounded-xl px-3 py-2 text-sm ${emailed ? "bg-sage/15 text-sage" : "bg-amber-deep/10 text-amber-deep"}`}>
              {emailed
                ? `Une copie a été envoyée à ${recoveryEmail}.`
                : `L'envoi par e-mail à ${recoveryEmail} est momentanément indisponible — notez bien ces codes dès maintenant.`}
            </p>
          )}
          <ul data-testid="twofa-recovery-codes" className="mt-4 grid grid-cols-2 gap-2 font-mono text-sm sm:grid-cols-4">
            {recoveryCodes.map((c) => (
              <li key={c} className="rounded-lg bg-white px-3 py-2 text-center text-bark">{c}</li>
            ))}
          </ul>
          <div className="mt-4 flex flex-wrap gap-2">
            <button data-testid="twofa-copy" onClick={copyCodes} className="flex items-center gap-1.5 rounded-full border border-amber-800/25 bg-white px-4 py-2 text-sm font-medium text-bark hover:border-amber-deep hover:text-amber-deep"><Copy size={14} /> Copier</button>
            <button data-testid="twofa-download" onClick={downloadCodes} className="flex items-center gap-1.5 rounded-full border border-amber-800/25 bg-white px-4 py-2 text-sm font-medium text-bark hover:border-amber-deep hover:text-amber-deep"><Download size={14} /> Télécharger</button>
            <button data-testid="twofa-print" onClick={printCodes} className="flex items-center gap-1.5 rounded-full border border-amber-800/25 bg-white px-4 py-2 text-sm font-medium text-bark hover:border-amber-deep hover:text-amber-deep"><Printer size={14} /> Imprimer</button>
            <button data-testid="twofa-done" onClick={() => setStep("idle")} className="flex items-center gap-1.5 rounded-full bg-amber-deep px-5 py-2 text-sm font-semibold text-white hover:bg-amber-deep/90">J'ai noté mes codes</button>
          </div>
        </div>
      )}

      {/* Actions quand la 2FA est activée */}
      {status.enabled && step !== "codes" && step !== "enrolling" && (
        <>
          {/* Régénérer les codes de secours */}
          <div className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
            <p className="flex items-center gap-2 font-serif text-lg font-semibold text-bark"><KeyRound size={18} /> Codes de secours</p>
            <p className="mb-4 mt-1 text-sm text-bark-soft">Générez 8 nouveaux codes à usage unique (les anciens seront invalidés). Pratique si vous avez déjà utilisé plusieurs codes.</p>
            <ConfirmAction
              testidPrefix="twofa-regen"
              trigger={<><KeyRound size={16} /> Régénérer 8 nouveaux codes</>}
              triggerClass="flex items-center gap-2 rounded-full bg-amber-deep px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-deep/90"
              onConfirm={(p, c) => regenerate(p, c)}
            />
          </div>

          {/* 2FA obligatoire */}
          <div className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
            <p className="flex items-center gap-2 font-serif text-lg font-semibold text-bark"><Lock size={18} /> 2FA obligatoire</p>
            <p className="mb-4 mt-1 text-sm text-bark-soft">
              {status.required
                ? "La 2FA est obligatoire : elle ne peut pas être désactivée tant que l'obligation n'est pas retirée. Cela évite une désactivation par mégarde."
                : "Rendez la 2FA obligatoire pour empêcher qu'elle soit désactivée par accident."}
            </p>
            <ConfirmAction
              testidPrefix="twofa-enforce"
              trigger={status.required ? <><Lock size={16} /> Retirer l'obligation</> : <><Lock size={16} /> Rendre obligatoire</>}
              triggerClass={`flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold transition-colors ${status.required ? "border border-amber-800/25 bg-white text-bark hover:border-amber-deep hover:text-amber-deep" : "bg-amber-deep text-white hover:bg-amber-deep/90"}`}
              onConfirm={(p, c) => enforce(p, c, !status.required)}
            />
          </div>

          {/* Désactivation */}
          <div className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
            <p className="flex items-center gap-2 font-serif text-lg font-semibold text-bark"><ShieldOff size={18} /> Désactiver la 2FA</p>
            <p className="mb-4 mt-1 text-sm text-bark-soft">Confirmez avec votre mot de passe et un code (application ou code de secours).</p>
            <ConfirmAction
              testidPrefix="twofa-disable"
              danger
              disabled={status.required}
              disabledHint={status.required ? "Retirez d'abord l'obligation de 2FA pour pouvoir la désactiver." : ""}
              trigger={<><ShieldOff size={16} /> Désactiver la 2FA</>}
              triggerClass="flex items-center gap-2 rounded-full border border-terra/30 px-6 py-2.5 text-sm font-medium text-terra transition-colors hover:bg-terra/10"
              onConfirm={(p, c) => disable(p, c)}
            />
          </div>
        </>
      )}
    </div>
  );
}
